import { Link } from "@tanstack/react-router";
import { useCallback, useState, type ReactNode } from "react";

import {
  BlockedMenuItem,
  IconMenuButton,
  IconMenuButtonContent,
  IconMenuButtonTrigger,
  IconMenuLoading,
  MoreVertGlyph,
  PressableMenuItem,
} from "@/components/ui/icon-menu-button";
import { useCatalogItemUsage } from "@/hooks/catalog-item-usage";
import { useCatalogItemDetail } from "@/hooks/catalog-items";
import { useItemsError } from "@/hooks/items-error";
import type { CatalogListItem } from "@/lib/catalog-item";
import { addVariantBlock, deleteBlock } from "@/lib/catalog-item-detail";
import { LOAD_EDIT_ACCESS_ERROR } from "./item-actions-menu";

// The Item Search island card's kebab — the donor's
// `ItemSearchIsland/components/ItemCard/ActionsMenu.tsx` (#798).
//
// **Its own component, not a mode of the catalog list's `ItemActionsMenu`**
// (#450 R1 names them as two donors). The two differ in every row and in what
// they act on: this one offers `Add Variant`, `Edit item` and `Delete`; the
// list's offers the two core rows, `Edit item` and `Delete item`. This one acts
// on the **displayed variant** (`item={variant}`), the list's on the card's own
// item. And every reader of this one waits on the read, where only a Division
// Editor waits on the list's.
//
// The verbs are the host's (the island page answers Add Variant and Delete with
// dialogs of its own, #811); this menu only says which item.

/** What the island card hands its menu — the item it shows, and two verbs. */
export interface ItemSearchActionsMenuProps {
  /** The item the card is showing — the previewed variant, when there is one. */
  item: CatalogListItem;
  /** `disabled={isFetchingVariantsData}` — a variant's read is in flight. */
  disabled?: boolean;
  /**
   * Whether this session edits every item regardless of division — the
   * donor's `canEdit` answering its unrestricted roles before it reads the
   * usage's divisions (`usePermissions.ts:110-113`). It is what Edit item and
   * Delete fall back to when the read fails; a landed read answers with the
   * item's own `mayEdit`. The details route makes the same fallback.
   */
  mayEditAnyItem: boolean;
  onAddVariant: (item: CatalogListItem) => void;
  onDelete: (item: CatalogListItem) => void;
}

export function ItemSearchActionsMenu(props: ItemSearchActionsMenuProps) {
  // Whether any launch on this card has settled, answered or failed. The
  // donor's spinner is its usage read's `isLoading`, and RTK's `isLoading` is
  // a first load: the card's one lazy hook keeps its last *settled* result
  // across a new argument and a retry alike (`lastResult`), so once the first
  // read has settled no launch on the card waits behind the spinner again — a
  // new variant's or a retry after a failure included — and the rows draw from
  // what is held, updating in place when the read lands (#964 N17). Kept
  // here, above the keyed body, because the donor's hook outlives a variant
  // switch.
  const [settled, setSettled] = useState(false);
  // Whether the card's last launch failed — the same hook's `isError`, which
  // it keeps across a new argument until it is triggered again: the menu is
  // not keyed per variant (`ItemCard.tsx:173-176`), so the snackbar outlives a
  // variant switch and goes when the next launch starts.
  const [failed, setFailed] = useState(false);
  const onSettled = useCallback((answered: boolean) => {
    setSettled(true);
    setFailed(!answered);
  }, []);
  const onLaunched = useCallback(() => setFailed(false), []);
  // No `onClose` on the donor's `ItemsError` (`ActionsMenu.tsx:116-118`), so
  // its snackbar never times out — it goes when the next read clears it.
  useItemsError(failed, LOAD_EDIT_ACCESS_ERROR, { duration: Infinity });

  // Keyed on the item: a card switching variant hands this menu another item
  // in place, and that item's launch is its own.
  return (
    <ItemSearchActionsMenuBody
      key={props.item.id}
      {...props}
      settled={settled}
      onSettled={onSettled}
      onLaunched={onLaunched}
    />
  );
}

function ItemSearchActionsMenuBody({
  item,
  disabled = false,
  mayEditAnyItem,
  onAddVariant,
  onDelete,
  settled,
  onSettled,
  onLaunched,
}: ItemSearchActionsMenuProps & {
  settled: boolean;
  /** A launch's read settled — `answered` false when it failed or threw. */
  onSettled: (answered: boolean) => void;
  onLaunched: () => void;
}) {
  // Never enabled: the reads are the launches', as the donor's lazy query is.
  // The item detail answers both questions the donor's usage read does — the
  // count Delete waits on, and the edit permission (`mayEdit`, #666 D1).
  const detail = useCatalogItemDetail(item.id, { enabled: false });
  // The item's usage as every usage reader shares it — the donor's usage
  // counts and divisions, which only a usage read writes: `load-item`, behind
  // a variant preview and the dialog, ships none on purpose, so Delete stays
  // refused and division-scoped editing closed until they are fetched (falcon
  // `shared/router.py:92-99`). A launch here records what it read; a failed
  // read keeps what an earlier one answered, as the donor's cache does.
  const { answer, record } = useCatalogItemUsage(item.id);

  const { refetch } = detail;
  // On pointer-down, as the donor fires on `onMouseDownCapture` — "a very
  // strong indicator users wants to activate the menu" — and on the opening,
  // which is the donor's second call in `onClick` and the keyboard's only one.
  // `forceRefetch: () => true`: every launch reads; a launch during a read
  // joins it.
  const launch = useCallback(() => {
    onLaunched();
    // A read that throws has settled too, and its failure rows replace the
    // spinner as a refused answer's would.
    void refetch({ cancelRefetch: false })
      .then((result) => {
        if (!result.isSuccess || !result.data) return false;
        record(item.id, {
          mayEdit: result.data.mayEdit,
          usageCount: result.data.item.usageCount,
        });
        return true;
      })
      .catch(() => false)
      .then(onSettled);
  }, [refetch, onLaunched, onSettled, record, item.id]);

  let rows: ReactNode;
  if (!settled && answer === undefined) {
    // `isLoading` — only before the card's first settled read, and only while
    // no usage reader has answered for the item: RTK's first load reads the
    // shared entry. A later launch shows what is held while it reads again.
    rows = <IconMenuLoading />;
  } else {
    // The donor's `canEdit`: an unrestricted role first, whatever the item's
    // divisions say, and the landed read's verdict for everyone else.
    const mayEdit = mayEditAnyItem || (answer?.mayEdit ?? false);
    // `specificationCount ?? 1`: no landed read, no count, and Delete refuses.
    const deleteReason = deleteBlock(answer?.usageCount);
    const addVariantReason = addVariantBlock(item);
    rows = (
      <>
        {addVariantReason ? (
          // `placement='top'`, no callout (`ActionsMenu.tsx:160-171`).
          <BlockedMenuItem
            label="Add Variant"
            reason={addVariantReason}
            side="top"
            testIdPrefix="item-search-action"
          />
        ) : (
          <PressableMenuItem
            data-testid="item-search-action-Add Variant"
            onSelect={() => onAddVariant(item)}
          >
            Add Variant
          </PressableMenuItem>
        )}
        {mayEdit && (
          <>
            {/* `component={ReactRouterLink} to={/products/:id/edit}
                target='_blank'` — Atlas's edit route is `/items/:id/edit`. */}
            <PressableMenuItem asChild>
              <Link
                data-testid="item-search-action-Edit item"
                to="/items/$itemId/edit"
                params={{ itemId: String(item.id) }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Edit item
              </Link>
            </PressableMenuItem>
            {deleteReason ? (
              // `showCallout`, `placement='bottom'` (`ActionsMenu.tsx:186-196`).
              <BlockedMenuItem
                label="Delete"
                reason={deleteReason}
                side="bottom"
                arrow
                testIdPrefix="item-search-action"
              />
            ) : (
              <PressableMenuItem
                data-testid="item-search-action-Delete"
                onSelect={() => onDelete(item)}
              >
                Delete
              </PressableMenuItem>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <IconMenuButton onOpenChange={(open) => open && launch()}>
      {/* Named after the item, which the donor's `aria-label='More options'`
          is not — twenty identically named buttons in a grid is twenty
          controls a screen reader cannot tell apart, the catalog list's
          reason for the same name. */}
      <IconMenuButtonTrigger
        label={`More options for ${item.name}`}
        box="card-corner"
        disabled={disabled}
        onPointerDown={launch}
      >
        <MoreVertGlyph />
      </IconMenuButtonTrigger>
      {/* `anchorOrigin {vertical: 'top', horizontal: 'left'}` against
          `transformOrigin {vertical: 'top', horizontal: 'right'}`: the paper's
          top-right corner on the trigger's top-left, so it opens leftward over
          the card rather than below the trigger. `minWidth: '150px'` on the
          paper (`ActionsMenu.tsx:140-148`). */}
      {/* `body1`: the donor's labels are bare `MenuItem`s (`ActionsMenu.tsx:180-207`). */}
      <IconMenuButtonContent minWidthPx={150} side="left" align="start" rowVariant="body1">
        {rows}
      </IconMenuButtonContent>
    </IconMenuButton>
  );
}
